import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, Subject, Subscription, takeUntil } from 'rxjs';
import { Store } from '@ngrx/store';
import * as potentialCauseActions from '../../../store/actions/potential-cause.actions';
import { MessageService } from 'primeng/api';
import { PotentialCause } from '../../../models/potential-cause.model';

import {
  selectPotentialCause,
  selectPotentialCauseError,
  selectPotentialCauseLoading,
} from '../../../store/selectors/potential-cause.selector';
import { GLOBAL_TOAST } from '../../../shared/constants/global-key.constans';

@Component({
  selector: 'app-potential-cause',
  templateUrl: './potential-cause.component.html',
  styleUrl: './potential-cause.component.scss',
  standalone: false,
})
export class PotentialCauseComponent implements OnInit, OnChanges, OnDestroy {
  @Input() ruleUid: string | undefined = undefined;

  potentialCause: PotentialCause[] | undefined = undefined;
  private destroy$ = new Subject<void>();
  loading$ = this.store.select(selectPotentialCauseLoading);
  error$ = this.store.select(selectPotentialCauseError);

  // Modal properties
  showEditModal = false;

  get sortedPotentialCauses(): PotentialCause[] {
    if (!this.potentialCause) {
      return [];
    }

    return [...this.potentialCause].sort((a, b) => {
      // First sort by percentage (descending)
      if (a.percentage !== b.percentage) {
        return b.percentage - a.percentage;
      }

      // If percentages are equal, sort by description (ascending)
      return a.description.localeCompare(b.description);
    });
  }

  private getPriorityLevel(percentage: number): number {
    if (percentage >= 50) {
      return 0; // high
    } else if (percentage >= 25) {
      return 1; // medium
    } else {
      return 2; // low
    }
  }

  getColorClass(percentage: number): string {
    return `priority-${this.getPriorityLevel(percentage)}`;
  }

  getBarFillClass(percentage: number): string {
    return `bar-fill-${this.getPriorityLevel(percentage)}`;
  }

  getBarIconColorClass(percentage: number): string {
    return `dot-${this.getPriorityLevel(percentage)}`;
  }

  getBarColorClass(percentage: number): string {
    return `percentage-${this.getPriorityLevel(percentage)}`;
  }

  constructor(
    private store: Store,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ruleUid']) {
      const newRuleUid = changes['ruleUid'].currentValue;
      if (newRuleUid) {
        this.ruleUid = newRuleUid;
        this.loadInitialData();
      }
    }
  }
  ngOnInit(): void {
    this.subscribeToStore();
  }

  private loadInitialData(): void {
    this.store.dispatch(
      potentialCauseActions.loadPotentialCause({ ruleUid: this.ruleUid }),
    );
  }

  private subscribeToStore(): void {
    combineLatest([this.store.select(selectPotentialCause)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([potentialCause]) => {
        this.potentialCause = potentialCause;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openEditModal(): void {
    this.showEditModal = true;
  }

  onModalSubmit(result: any): void {
    this.handleModalResult(result);
    this.showEditModal = false;
  }

  onModalCancel(): void {
    this.showEditModal = false;
  }

  private async handleModalResult(result: any): Promise<void> {
    const { toCreate, toUpdate, toDelete } = result;

    // Dispatch actions to the store
    if (toDelete && toDelete.length > 0) {
      this.store.dispatch(
        potentialCauseActions.deleteMultiplePotentialCauses({ uids: toDelete }),
      );
    }

    if (toUpdate && toUpdate.length > 0) {
      const updateRequests = toUpdate.map((cause: any) => ({
        uid: cause.uid,
        description: cause.description,
        percentage: cause.percentage,
        ruleUid: this.ruleUid!,
      }));
      this.store.dispatch(
        potentialCauseActions.updateMultiplePotentialCauses({
          requests: updateRequests,
        }),
      );
    }

    if (toCreate && toCreate.length > 0) {
      const createRequests = toCreate.map((cause: any) => ({
        description: cause.description,
        percentage: cause.percentage,
        ruleUid: this.ruleUid!,
      }));
      this.store.dispatch(
        potentialCauseActions.createMultiplePotentialCauses({
          requests: createRequests,
        }),
      );
    }

    this.messageService.add({
      key: GLOBAL_TOAST,
      severity: 'success',
      summary: 'Succès',
      detail: 'Les modifications ont été envoyées.',
    });
  }
}
